// Step 5 — Import Control intake review (app.md §9). Queue of invoices held at
// Pending Import Control Decision.
//
// A matching failure (question 4.11, 2026-08-12) arrives here as an ORDINARY
// row: the match worker's failure path lands the invoice at this same status
// carrying a `MatchingFailed` flag, rather than a separate status this screen
// had to query for specially. `Matching Failed` stopped being a status
// because it was a dead end — no save could reach it and no decision could
// act on it — and it was invisible to this queue's own timeline badge. The
// flag pill alone carries the signal now. Import Control still settles the
// Company, and nothing else (issue 3, 2026-08-11, `docs/workflow.md` §5,
// owner ruling 4) — every other field is locked, since
// vendor/amounts/flags/expense-types are AP/AR's to fix next, including
// marking `MatchingFailed` itself resolved: this screen's editable surface
// offers no "Mark resolved" control while `atImportControl` is true, so that
// flag rides through Import Control unresolved like `MissingCompany` used to,
// and clears only once AP/AR's own release gate is reached.
// This is a click-into-each-invoice surface (no bulk) — each held invoice needs
// different fixes. The detail modal opens in EDIT mode at Import Control's own
// gate (`editStep={DecisionStep.ImportControl}`), and its Save & Release both
// saves and releases in one request.
//
// PORT NOTE: release used to be a decision write the reviewer could only hope
// had landed — a background process advanced the invoice, so the row sat at
// "Submitting…" while the queue re-polled for up to half a minute. The release is
// applied synchronously now, so acting on an invoice means reloading the queue
// and finding it gone. The flag chips read each row's own unresolved flag types
// (there was a second query per queue load for them).
import { useMemo, useState } from 'react'
import { Body1, Toast, ToastTitle, makeStyles, tokens, useToastController } from '@fluentui/react-components'
import { ScreenTitle } from '../components/ScreenTitle'
import type { CurrentUser, Invoice } from '../lib/types'
import { DecisionStep, InvoiceStatus, SCREEN_SUBHEADER, TOASTER_ID } from '../lib/constants'
import { useQueue } from '../lib/hooks'
import { InvoiceQueue } from '../components/InvoiceQueue'
import { InvoiceDetailModal } from '../components/InvoiceDetailModal'
import { applyFilters, type InvoiceFilter } from '../lib/filters'
import { Loading, EmptyQueue, ErrorBanner } from '../components/States'

const useStyles = makeStyles({
  header: { display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap', marginBottom: '12px' },
  sub: { color: tokens.colorNeutralForeground3 },
})

interface Props {
  user: CurrentUser
  refreshNonce?: number
  /** Page-wide filter (header Filter button; also drives the timeline badges). */
  filter: InvoiceFilter
  /** Called when acted rows leave the queue, so the timeline badges refresh too. */
  onQueuesChanged?: () => void
}

export function ImportControlScreen({ user, refreshNonce, filter, onQueuesChanged }: Props) {
  const s = useStyles()
  const { invoices, loading, error, reload } = useQueue([InvoiceStatus.PendingImportControl], refreshNonce)
  const [openInvoice, setOpenInvoice] = useState<Invoice | null>(null)
  const { dispatchToast } = useToastController(TOASTER_ID)

  const filtered = useMemo(() => applyFilters(invoices, filter), [invoices, filter])

  // The release already happened by the time this runs — confirm it, then reload
  // so the invoice drops out of the queue.
  function handleReleased() {
    dispatchToast(
      <Toast>
        <ToastTitle>Invoice released for approval</ToastTitle>
      </Toast>,
      { intent: 'success' },
    )
    void reload()
    onQueuesChanged?.()
  }

  function handleArchived() {
    dispatchToast(
      <Toast>
        <ToastTitle>Archived — Not an Invoice</ToastTitle>
      </Toast>,
      { intent: 'success' },
    )
    void reload()
    onQueuesChanged?.()
  }

  return (
    <div>
      <div className={s.header}>
        <ScreenTitle>Import Control</ScreenTitle>
        <Body1 className={s.sub}>{SCREEN_SUBHEADER.ImportControl}</Body1>
      </div>

      <ErrorBanner message={error} />

      {loading ? (
        <Loading label="Loading held invoices…" />
      ) : filtered.length === 0 ? (
        <EmptyQueue text={invoices.length > 0 ? 'No invoices match the current filters.' : undefined} />
      ) : (
        <InvoiceQueue invoices={filtered} showFlags onOpen={setOpenInvoice} />
      )}

      <InvoiceDetailModal
        open={!!openInvoice}
        invoice={openInvoice}
        showPdf
        editStep={DecisionStep.ImportControl}
        user={user}
        onClose={() => setOpenInvoice(null)}
        onChanged={() => void reload({ silent: true })}
        onReleased={handleReleased}
        onArchived={handleArchived}
      />
    </div>
  )
}
